import { useSyncExternalStore } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { INVENTED_READINESS_COPY } from '../../platform/config/camera-readiness';
import { useAppServices } from './services-context';

const NAV_LINKS = [
  { to: '/catalogue', label: 'Games' },
  { to: '/profiles', label: 'Players' },
  { to: '/parent', label: 'Parent zone' },
];

function linkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'app-shell__link app-shell__link--active' : 'app-shell__link';
}

export function AppLayout() {
  const { activePlayer, storageBackend, camera } = useAppServices();

  /**
   * The camera-active indicator (architecture §23: "Clear camera indicator").
   *
   * It lives in the shell, not on the readiness screen, so that it is true from anywhere in the
   * app and cannot be left behind by a route change. It reads the narrowest possible selector —
   * a boolean — so four readings a second do not re-render the whole shell.
   */
  const cameraActive = useSyncExternalStore(
    camera.subscribe,
    camera.isStreamActive,
    camera.isStreamActive,
  );

  return (
    <div className="app-shell">
      <nav className="app-shell__nav" aria-label="Main">
        <NavLink to="/" className="app-shell__brand">
          Thrive Games
        </NavLink>
        {NAV_LINKS.map((link) => (
          <NavLink key={link.to} to={link.to} className={linkClass}>
            {link.label}
          </NavLink>
        ))}
        <span className="badge" data-testid="active-player">
          {activePlayer === null ? 'No player chosen' : activePlayer.label}
        </span>
        {cameraActive ? (
          <span className="camera-indicator" data-testid="camera-active-indicator" role="status">
            {INVENTED_READINESS_COPY.cameraActive}
          </span>
        ) : null}
      </nav>

      {storageBackend === 'local-storage' ? null : (
        <p className="notice" role="status" data-testid="storage-notice">
          {storageBackend === 'read-only'
            ? 'Thrive Games has run out of room to save, so anything from now on will disappear when you close the tab. Everything already saved is safe.'
            : 'This browser is not letting Thrive Games save anything, so progress will disappear when you close the tab. Everything else works normally.'}
        </p>
      )}

      <main className="app-shell__main">
        <Outlet />
      </main>
    </div>
  );
}
